import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../../../../store/storeAuth";
import { useObjectStore } from "../../../../../store/storeObjects";
import useFlowsStore from "../../../../../store/storeFlows";
import { useNotificationStore, NotificationType } from "../../../../../store/storeNotifications";
import { UserService } from "../../../../../service/UserService";
import { FolderService } from "../../../../../service/FolderService";
import { Folder } from "../../../../../util/Types/Folder";
import { CreateItemType, SpaceOption } from "../../types";

export const useCreateItemModal = (type: CreateItemType, onHide: () => void) => {
  const navigate = useNavigate();
  const { setAuthUser, setFolders } = useAuthStore((state) => state);
  const { setObjectUpdate } = useObjectStore((state) => state);
  const { addFlow } = useFlowsStore((state) => state);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const [name, setName] = useState("");
  const [selectedSpaceId, setSelectedSpaceId] = useState<string>("private");
  const [spaces, setSpaces] = useState<SpaceOption[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [showVectorModal, setShowVectorModal] = useState(false);
  const [showObjectModal, setShowObjectModal] = useState(false);
  const [createdObjectSpaceId, setCreatedObjectSpaceId] = useState<string>("user");
  const [vectorMemorySpaceId, setVectorMemorySpaceId] = useState<string | undefined>(undefined);
  const [showInterfaceModal, setShowInterfaceModal] = useState(false);
  const [isCreateFormLoading, setIsCreateFormLoading] = useState(false);

  useEffect(() => {
    loadSpaces();
  }, []);

  const loadSpaces = async () => {
    try {
      const { data } = await UserService.getSpaces();
      setSpaces(data);
    } catch (error) {
      console.error("Failed to load spaces:", error);
    }
  };

  const findFolderRecursively = (
    folders: Folder[],
    names: string[],
  ): Folder | undefined => {
    const lowerNames = names.map((n) => n.toLowerCase());
    for (const folder of folders) {
      if (lowerNames.includes(folder.name?.toLowerCase())) {
        return folder;
      }
      if (folder.folders && folder.folders.length > 0) {
        const found = findFolderRecursively(folder.folders, names);
        if (found) return found;
      }
    }
    return undefined;
  };

  const getFolderAliases = (folderName: string): string[] => {
    if (folderName === "Fluxbots") {
      return ["Fluxbots", "chatbot"];
    }
    return [folderName];
  };

  const ensureFolderExists = async (
    folderName: string,
    spaceId?: string,
  ): Promise<string | null> => {
    try {
      let targetFolders: Folder[] = [];

      if (spaceId) {
        const spacesResponse = await UserService.getSpaces();
        const space = spacesResponse.data.find(
          (s: SpaceOption) => s.id === spaceId,
        );
        targetFolders = space?.folder || [];
      } else {
        const foldersResponse = await FolderService.getFolders();
        targetFolders = foldersResponse.data || [];
        setFolders(foldersResponse.data || []);
      }

      const aliases = getFolderAliases(folderName);
      const existingFolder = findFolderRecursively(targetFolders, aliases);
      if (existingFolder) {
        return existingFolder.id;
      }

      const folderData = spaceId
        ? await FolderService.createFolderOnSpace({ name: folderName, spaceId })
        : await FolderService.createFolder({ name: folderName });

      if (spaceId) {
        const spacesResponse = await UserService.getSpaces();
        const updatedSpaces = spacesResponse.data.map(
          (s: SpaceOption & { folder?: Folder[] }) =>
            s.id === spaceId
              ? { ...s, folder: [...(s.folder || []), folderData.data] }
              : s,
        );
        setSpaces(updatedSpaces);
      } else {
        const foldersResponse = await FolderService.getFolders();
        setFolders(foldersResponse.data || []);
      }

      return folderData.data.id;
    } catch (error) {
      console.error(`Failed to create folder ${folderName}:`, error);
      setNotification({
        type: NotificationType.Error,
        message: `Failed to create folder ${folderName}`,
      });
      return null;
    }
  };

  const resetForm = () => {
    setName("");
    setSelectedSpaceId("private");
  };

  return {
    name,
    setName,
    selectedSpaceId,
    setSelectedSpaceId,
    spaces,
    isCreating,
    setIsCreating,
    showVectorModal,
    setShowVectorModal,
    showObjectModal,
    setShowObjectModal,
    createdObjectSpaceId,
    setCreatedObjectSpaceId,
    vectorMemorySpaceId,
    setVectorMemorySpaceId,
    showInterfaceModal,
    setShowInterfaceModal,
    isCreateFormLoading,
    setIsCreateFormLoading,
    ensureFolderExists,
    loadSpaces,
    resetForm,
    navigate,
    setAuthUser,
    addFlow,
    setNotification,
    setObjectUpdate,
  };
};
